import { useState } from 'react'
import {
  MAXIMO_DE_VACANTES,
  MINIMO_DE_VACANTES,
  vacantesDesdeInput,
} from '@/features/rh/lib/numeroDeVacantes'
import { Send, X } from 'lucide-react'
import { useJobRequestActions } from '@/hooks/useJobRequestActions'
import { esHtmlVacio } from '@/features/rh/lib/descripcionDelPuesto'
import type { JobRequest } from '@/services/api'
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton'

interface RespondToReturnedProps {
  request: JobRequest
  onSuccess: () => void
}

export const RespondToReturned = ({ request, onSuccess }: RespondToReturnedProps) => {
  const [respuesta, setRespuesta] = useState('')
  const [justificacionNecesidad, setJustificacionNecesidad] = useState(request.justificacionNecesidad || '')
  const [numeroPlazas, setNumeroPlazas] = useState(request.numeroPlazas || 1)
  const [fechaRequeridaInicio, setFechaRequeridaInicio] = useState(request.fechaRequeridaInicio)
  const [habilidadesMinimas, setHabilidadesMinimas] = useState(request.habilidadesMinimas)
  const [showForm, setShowForm] = useState(false)
  const [errorLocal, setErrorLocal] = useState<string | null>(null)
  
  const { loading, error, respondToReturned, clearError } = useJobRequestActions()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrorLocal(null)
    
    const updatedData: any = {}
    
    // Solo incluir campos que han cambiado
    if (justificacionNecesidad !== (request.justificacionNecesidad || '')) {
      updatedData.justificacionNecesidad = justificacionNecesidad
    }
    
    if (numeroPlazas !== (request.numeroPlazas || 1)) {
      updatedData.numeroPlazas = numeroPlazas
    }
    
    if (fechaRequeridaInicio !== request.fechaRequeridaInicio) {
      updatedData.fechaRequeridaInicio = fechaRequeridaInicio
    }
    
    if (habilidadesMinimas !== request.habilidadesMinimas) {
      // Borrar las habilidades no es una respuesta válida: el servidor las exige.
      if (esHtmlVacio(habilidadesMinimas)) {
        setErrorLocal('Las habilidades mínimas son obligatorias')
        return
      }
      updatedData.habilidadesMinimas = habilidadesMinimas
    }

    const result = await respondToReturned(request.id.toString(), respuesta, updatedData)
    
    if (result) {
      onSuccess()
      setShowForm(false)
    }
  }

  const resetForm = () => {
    setRespuesta('')
    setJustificacionNecesidad(request.justificacionNecesidad || '')
    setNumeroPlazas(request.numeroPlazas || 1)
    setFechaRequeridaInicio(request.fechaRequeridaInicio)
    setHabilidadesMinimas(request.habilidadesMinimas)
    setShowForm(false)
    clearError()
  }

  if (!showForm) {
    return (
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-blue-900 font-medium">Solicitud Devuelta</h3>
            <p className="text-blue-700 text-sm">
              Esta solicitud requiere información adicional antes de continuar con el proceso.
            </p>
          </div>
          <button
            onClick={() => setShowForm(true)}
            className={estiloDeBoton('primario')}
          >
            Responder
          </button>
        </div>
        
        {/* Mostrar comentarios de devolución */}
        {request.motivoDevolucion && (
          <div className="mt-4 p-3 bg-yellow-50 rounded border-l-4 border-yellow-400">
            <h4 className="font-medium text-yellow-900 mb-1">Motivo de Devolución:</h4>
            <p className="text-yellow-800">{request.motivoDevolucion}</p>
          </div>
        )}
        
        {request.comentariosHr && (
          <div className="mt-4 p-3 bg-blue-50 rounded border-l-4 border-blue-400">
            <h4 className="font-medium text-blue-900 mb-1">Comentarios de RH:</h4>
            <p className="text-blue-800">{request.comentariosHr}</p>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900">Responder a Solicitud Devuelta</h3>
        <button
          onClick={resetForm}
          className="text-gray-400 hover:text-gray-600"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {(errorLocal || error) && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
          <p className="text-red-700 text-sm">{errorLocal || error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Respuesta */}
        <div>
          <label htmlFor="respuesta" className="block text-sm font-medium text-gray-700 mb-2">
            Respuesta *
          </label>
          <textarea
            id="respuesta"
            value={respuesta}
            onChange={(e) => setRespuesta(e.target.value)}
            required
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Describe los cambios realizados y responde a los comentarios..."
          />
        </div>

        {/* Campos actualizables */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="numeroPlazas" className="block text-sm font-medium text-gray-700 mb-2">
              Número de Vacantes
            </label>
            <input
              type="number"
              id="numeroPlazas"
              value={numeroPlazas}
              onChange={(e) => setNumeroPlazas(vacantesDesdeInput(e.target.value))}
              min={MINIMO_DE_VACANTES}
              max={MAXIMO_DE_VACANTES}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label htmlFor="fechaEstimadaInicio" className="block text-sm font-medium text-gray-700 mb-2">
              Fecha Requerida de Inicio
            </label>
            <input
              type="date"
              id="fechaRequeridaInicio"
              value={fechaRequeridaInicio}
              onChange={(e) => setFechaRequeridaInicio(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        <div>
          <label htmlFor="habilidadesMinimas" className="block text-sm font-medium text-gray-700 mb-2">
            Habilidades Mínimas
          </label>
          <textarea
            id="habilidadesMinimas"
            value={habilidadesMinimas}
            onChange={(e) => setHabilidadesMinimas(e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        <div>
          <label htmlFor="justificacionNecesidad" className="block text-sm font-medium text-gray-700 mb-2">
            Justificación de la Necesidad
          </label>
          <textarea
            id="justificacionNecesidad"
            value={justificacionNecesidad}
            onChange={(e) => setJustificacionNecesidad(e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* Botones */}
        <div className="flex justify-end space-x-3 pt-4">
          <button
            type="button"
            onClick={resetForm}
            className={estiloDeBoton('secundario')}
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={loading || !respuesta.trim()}
            className={estiloDeBoton('primario')}
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Enviando...
              </>
            ) : (
              <>
                <Send className="w-4 h-4" />
                Enviar Respuesta
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  )
}
